import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useAdminDashboardStats } from '../api/useAdminDashboard';
import { AdminAPI } from '@shared/types/api';

function formatCountdown(msUntil: number): string {
  if (msUntil <= 0) return 'now';
  const totalSeconds = Math.floor(msUntil / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatInZone(startsAt: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(startsAt));
}

function CountdownList({
  events,
  expanded,
}: {
  events: AdminAPI.NextEventByCity[];
  expanded: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (events.length === 0) {
    return (
      <div className="text-dark-muted text-sm">No upcoming events scheduled.</div>
    );
  }

  const groups = new Map<string, { sortMs: number; cities: AdminAPI.NextEventByCity[] }>();
  for (const ev of events) {
    const startsAtMs = new Date(ev.startsAt).getTime();
    const label = formatCountdown(startsAtMs - now);
    const existing = groups.get(label);
    if (existing) {
      existing.cities.push(ev);
      if (startsAtMs < existing.sortMs) existing.sortMs = startsAtMs;
    } else {
      groups.set(label, { sortMs: startsAtMs, cities: [ev] });
    }
  }
  const sortedGroups = Array.from(groups.entries()).sort(
    (a, b) => a[1].sortMs - b[1].sortMs
  );
  const visibleGroups = expanded ? sortedGroups : sortedGroups.slice(0, 1);

  return (
    <div className="space-y-2">
      {visibleGroups.map(([label, { sortMs, cities }]) => {
        const representative = cities[0];
        const startsAtIso = new Date(sortMs).toISOString();
        return (
          <div
            key={label}
            className="rounded-xl border border-divider px-4 py-3"
          >
            <div className="flex items-center justify-stretch gap-3">
              <span className="font-mono font-semibold text-primary-400 tabular-nums shrink-0">
                {label}
              </span>
              <div className="text-sm text-dark-muted tabular-nums mt-1 flex-1">
                {formatInZone(startsAtIso, representative.timezone)}{' '}
                <span className="opacity-70">(event)</span>
                <span className="mx-1">·</span>
                {formatInZone(startsAtIso)}
              </div>
              <span className="font-medium text-right truncate">
                {cities.map((c) => `${c.emoji} ${c.cityName}`).join(', ')}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface NextEventsTimerProps {
  delay?: number;
  className?: string;
}

export default function NextEventsTimer({ delay = 0.05, className = '' }: NextEventsTimerProps) {
  const { data: statsData, isLoading } = useAdminDashboardStats();
  const [expanded, setExpanded] = useState(false);
  const nextEventsByCity = statsData?.data?.nextEventsByCity || [];

  const distinctStartTimes = new Set(
    nextEventsByCity.map((e) => new Date(e.startsAt).getTime())
  ).size;
  const canExpand = distinctStartTimes > 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`card mb-8 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Next Events</h2>
        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-sm text-primary-400 hover:text-primary-300"
          >
            {expanded ? 'Collapse' : `Expand (${distinctStartTimes})`}
          </button>
        )}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="w-8 h-8 border-2 border-primary-50 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <CountdownList events={nextEventsByCity} expanded={expanded} />
      )}
    </motion.div>
  );
}
